<template>
  <div class="scan-page">
    <!-- 安卓微信：提示在浏览器打开 -->
    <div v-if="isWechatAndroid" class="view-wechat-android">
      <div class="wxa-icon">🌐</div>
      <div class="wxa-title">请在手机浏览器中打开</div>
      <div class="wxa-desc">微信内暂不支持文件下载，请按以下步骤操作：</div>
      <div class="wxa-steps">
        <div class="wxa-step">
          <span class="wxa-step-num">1</span>
          <span>点击右上角 <strong>「···」</strong> 按钮</span>
        </div>
        <div class="wxa-step">
          <span class="wxa-step-num">2</span>
          <span>选择 <strong>「在浏览器中打开」</strong></span>
        </div>
        <div class="wxa-step">
          <span class="wxa-step-num">3</span>
          <span>在浏览器中填写信息并下载资料</span>
        </div>
      </div>
      <div class="wxa-arrow">↗ 点击右上角「···」</div>
    </div>

    <!-- 加载中 -->
    <div v-else-if="loading" class="full-center">
      <a-spin size="large" />
    </div>

    <!-- 活动异常 -->
    <div v-else-if="errorMsg" class="full-center">
      <div class="error-wrap">
        <div class="error-icon">😔</div>
        <div class="error-title">{{ errorMsg }}</div>
        <div class="error-sub">请联系活动主办方获取最新信息</div>
      </div>
    </div>

    <!-- 正常内容 -->
    <template v-else-if="activityInfo">
      <!-- 填写信息 -->
      <div v-if="step === 'form'" class="view-form">
        <div class="form-title">填写您的信息</div>
        <div class="form-sub">验证后即可获取资料</div>

        <div class="field-group">
          <div class="field">
            <label class="field-label">姓名 <span class="req">*</span></label>
            <input
              v-model="form.visitorName"
              class="field-input"
              type="text"
              placeholder="请输入您的姓名"
              autocomplete="name"
            />
            <div v-if="errors.visitorName" class="field-error">{{ errors.visitorName }}</div>
          </div>

          <div class="field">
            <label class="field-label">组织 / 企业 <span class="req">*</span></label>
            <input
              v-model="form.orgName"
              class="field-input"
              type="text"
              placeholder="请输入您的单位名称"
              autocomplete="organization"
            />
            <div v-if="errors.orgName" class="field-error">{{ errors.orgName }}</div>
          </div>

          <div class="field">
            <label class="field-label">职位 <span class="req">*</span></label>
            <input
              v-model="form.position"
              class="field-input"
              type="text"
              placeholder="请输入您的职位"
            />
            <div v-if="errors.position" class="field-error">{{ errors.position }}</div>
          </div>

          <div class="field">
            <label class="field-label">手机号 <span class="req">*</span></label>
            <input
              v-model="form.phone"
              class="field-input"
              type="tel"
              placeholder="请输入手机号"
              inputmode="numeric"
              maxlength="11"
              autocomplete="tel"
            />
            <div v-if="errors.phone" class="field-error">{{ errors.phone }}</div>
          </div>

          <div class="field">
            <label class="field-label">验证码 <span class="req">*</span></label>
            <div class="code-row">
              <input
                v-model="form.verifyCode"
                class="field-input code-input"
                type="text"
                placeholder="请输入验证码"
                inputmode="numeric"
                maxlength="6"
              />
              <button
                class="btn-code"
                :disabled="countdown > 0 || sendingCode"
                @click="handleSendCode"
              >
                <template v-if="sendingCode">发送中…</template>
                <template v-else-if="countdown > 0">{{ countdown }}s后重发</template>
                <template v-else>发送验证码</template>
              </button>
            </div>
            <div v-if="errors.verifyCode" class="field-error">{{ errors.verifyCode }}</div>
            <div v-if="codeError" class="field-error">{{ codeError }}</div>
          </div>
        </div>

        <div v-if="submitError" class="submit-error">{{ submitError }}</div>

        <button
          class="btn-submit"
          :disabled="submitLoading"
          @click="handleSubmit"
        >
          <span v-if="submitLoading" class="loading-spin"></span>
          {{ submitLoading ? '提交中…' : '立即获取资料' }}
        </button>
      </div>

      <!-- 下载资料 -->
      <div v-else-if="step === 'download'" class="view-download">
        <!-- 顶部成功横幅 -->
        <div class="dl-banner">
          <div class="dl-banner-icon">✓</div>
          <div class="dl-banner-info">
            <div class="dl-banner-title">验证成功</div>
            <div class="dl-banner-sub">共 {{ (activityInfo.materials || []).length }} 份资料</div>
          </div>
          <button class="btn-dl-all" :disabled="selectedMaterials.length === 0" @click="handleDownloadSelected">下载</button>
        </div>

        <!-- iOS 微信提示 -->
        <div v-if="isWechatIOS" class="dl-ios-tip">
          <div class="dl-ios-tip-title">📱 iOS 保存文件步骤</div>
          <div class="dl-ios-tip-body">
            <div class="dl-ios-step">① 选中文件</div>
            <div class="dl-ios-step">② 点击下载按钮</div>
          </div>
        </div>

        <!-- 无资料 -->
        <div v-if="!activityInfo.materials || activityInfo.materials.length === 0" class="dl-empty">
          暂无可下载资料，请联系活动主办方
        </div>

        <div v-else class="dl-list-wrap">
          <!-- 全选行 -->
          <div class="dl-row dl-row-header" @click="handleSelectAll">
            <span class="xz-checkbox" :class="{ 'is-checked': isAllSelected, 'is-indeterminate': isIndeterminate }">
              <span class="xz-checkbox-inner"></span>
            </span>
            <span class="dl-row-all-label">
              全选
              <span v-if="selectedMaterials.length > 0" class="dl-count-badge">{{ selectedMaterials.length }}</span>
            </span>
          </div>

          <!-- 文件行 -->
          <div
            v-for="item in activityInfo.materials"
            :key="item.id"
            class="dl-row"
            :class="{ 'is-selected': selectedMaterials.includes(item.id) }"
            @click="handleToggleSelect(item.id)"
          >
            <span class="xz-checkbox" :class="{ 'is-checked': selectedMaterials.includes(item.id) }">
              <span class="xz-checkbox-inner"></span>
            </span>
            <span class="dl-row-thumb" :class="thumbBgClass(item.fileName)">
              <a-icon v-if="isPdf(item.fileName)" type="file-pdf" class="thumb-pdf" />
              <a-icon v-else-if="isWord(item.fileName)" type="file-word" class="thumb-word" />
              <a-icon v-else-if="isExcel(item.fileName)" type="file-excel" class="thumb-excel" />
              <a-icon v-else type="file" class="thumb-default" />
            </span>
            <span class="dl-row-name">{{ item.name }}</span>
            <a :href="getDownloadUrl(item.id)" target="_blank" rel="noopener" class="dl-row-preview" @click.stop>
              <a-icon type="eye" />
            </a>
          </div>
        </div>

        <!-- 固定底部操作栏 -->
        <div class="dl-action-bar">
          <span class="dl-action-hint">
            {{ selectedMaterials.length > 0 ? `已选 ${selectedMaterials.length} 个文件` : '请勾选要下载的文件' }}
          </span>
          <button class="btn-dl-selected" :disabled="selectedMaterials.length === 0 || downloading" @click="handleDownloadSelected">
            下载所选
          </button>
        </div>
      </div>
    </template>

    <!-- 下载进度遮罩 -->
    <div v-if="downloading" class="dl-progress-mask">
      <div class="dl-progress-box">
        <div class="dl-progress-title">
          {{ downloadProgress >= 90 ? '正在打包…' : '正在下载文件…' }}
        </div>
        <div class="dl-progress-bar-wrap">
          <div class="dl-progress-bar" :style="{ width: downloadProgress + '%' }"></div>
        </div>
        <div class="dl-progress-text">
          {{ downloadProgress > 0 ? downloadProgress + '%' : '准备中…' }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import JSZip from 'jszip';
import {
  openActivityDetail,
  openActivitySendVerifyCode,
  openActivityVerifyAndRecord,
  getActivityDownloadUrl,
} from '@/api/activity.js';

const CACHE_TTL = 60 * 60 * 1000; // 1小时

export default {
  name: 'ActivityScan',

  data () {
    return {
      loading: true,
      errorMsg: '',
      activityInfo: null,
      step: 'form', // 'form' | 'download'
      visitorId: null,

      form: {
        visitorName: '',
        orgName: '',
        position: '',
        phone: '',
        verifyCode: '',
      },
      errors: {},

      sendingCode: false,
      countdown: 0,
      countdownTimer: null,
      submitLoading: false,
      submitError: '',
      codeError: '',
      selectedMaterials: [],
      downloading: false,
      downloadProgress: 0,
    };
  },

  computed: {
    activityId () {
      return this.$route.query.activityId;
    },
    cacheKey () {
      return `xz_scan_visitor_${this.activityId}`;
    },
    isWeChat () {
      return /MicroMessenger/i.test(navigator.userAgent);
    },
    isIOS () {
      return /iPhone|iPad|iPod/i.test(navigator.userAgent);
    },
    isWechatAndroid () {
      return this.isWeChat && !this.isIOS;
    },
    isWechatIOS () {
      return this.isWeChat && this.isIOS;
    },
    isAllSelected () {
      const list = this.activityInfo?.materials || [];
      return list.length > 0 && this.selectedMaterials.length === list.length;
    },
    isIndeterminate () {
      return this.selectedMaterials.length > 0 && !this.isAllSelected;
    },
  },

  async created () {
    // 安卓微信内置浏览器：直接显示引导页，无需加载活动数据
    if (this.isWechatAndroid) {
      this.loading = false;
      return;
    }
    if (!this.activityId) {
      this.errorMsg = '活动不存在或链接无效';
      this.loading = false;
      return;
    }
    try {
      const { body } = await openActivityDetail({ activityId: this.activityId });
      this.activityInfo = body;
      // 检查是否有1小时内的有效缓存，有则跳过填写直接展示资料
      this.checkCache();
    } catch (e) {
      this.errorMsg = '活动已结束';
    } finally {
      this.loading = false;
    }
  },

  mounted () {
    document.documentElement.style.overflowX = 'hidden';
    document.body.style.overflowX = 'hidden';
    document.body.style.background = '#f5f7fa';
  },

  beforeDestroy () {
    if (this.countdownTimer) clearInterval(this.countdownTimer);
    document.documentElement.style.overflowX = '';
    document.body.style.overflowX = '';
    document.body.style.background = '';
  },

  methods: {
    checkCache () {
      try {
        const raw = localStorage.getItem(this.cacheKey);
        if (raw) {
          const { time, visitorId } = JSON.parse(raw);
          if (Date.now() - time < CACHE_TTL && visitorId) {
            this.visitorId = visitorId;
            this.step = 'download';
          }
        }
      } catch (e) {
        // 缓存解析失败忽略
      }
    },

    saveCache (visitorId) {
      try {
        localStorage.setItem(this.cacheKey, JSON.stringify({ time: Date.now(), visitorId }));
      } catch (e) {
        // ignore
      }
    },

    validateForm () {
      const errors = {};
      const { visitorName, orgName, position, phone, verifyCode } = this.form;
      if (!visitorName || visitorName.trim().length < 2) errors.visitorName = '姓名不少于2个字符';
      if (!orgName || orgName.trim().length < 3) errors.orgName = '单位名称不少于3个字符';
      if (!position || !position.trim()) errors.position = '请输入职位';
      if (!phone || !/^1[3-9]\d{9}$/.test(phone)) errors.phone = '请输入正确的手机号';
      if (!verifyCode || verifyCode.length !== 6) errors.verifyCode = '验证码为6位数字';
      this.errors = errors;
      return Object.keys(errors).length === 0;
    },

    async handleSendCode () {
      const { phone } = this.form;
      if (!phone || !/^1[3-9]\d{9}$/.test(phone)) {
        this.errors = { ...this.errors, phone: '请输入正确的手机号' };
        return;
      }
      this.errors = { ...this.errors, phone: '' };
      this.codeError = '';
      this.sendingCode = true;
      try {
        await openActivitySendVerifyCode({ activityId: this.activityId, phone }, { stopDefaultMessage: true });
        this.startCountdown();
      } catch (err) {
        this.codeError = err?.heads?.message || '验证码发送失败，请重试';
      } finally {
        this.sendingCode = false;
      }
    },

    startCountdown () {
      this.countdown = 60;
      this.countdownTimer = setInterval(() => {
        this.countdown--;
        if (this.countdown <= 0) clearInterval(this.countdownTimer);
      }, 1000);
    },

    async handleSubmit () {
      if (!this.validateForm()) return;
      this.submitLoading = true;
      this.submitError = '';
      try {
        const allMaterialIds = (this.activityInfo.materials || []).map((m) => m.id);
        const { body: visitorId } = await openActivityVerifyAndRecord({
          activityId: this.activityId,
          visitorName: this.form.visitorName,
          orgName: this.form.orgName,
          position: this.form.position,
          phone: this.form.phone,
          verifyCode: this.form.verifyCode,
          materialIds: allMaterialIds,
        }, { stopDefaultMessage: true });
        this.visitorId = visitorId;
        this.saveCache(visitorId);
        this.step = 'download';
      } catch (err) {
        this.submitError = err?.heads?.message || '提交失败，请重试';
      } finally {
        this.submitLoading = false;
      }
    },

    handleToggleSelect (id) {
      const idx = this.selectedMaterials.indexOf(id);
      if (idx === -1) {
        this.selectedMaterials.push(id);
      } else {
        this.selectedMaterials.splice(idx, 1);
      }
    },

    handleSelectAll () {
      if (this.isAllSelected) {
        this.selectedMaterials = [];
      } else {
        this.selectedMaterials = (this.activityInfo?.materials || []).map((m) => m.id);
      }
    },

    getDownloadUrl (materialId) {
      return getActivityDownloadUrl(this.visitorId, materialId);
    },

    handleDownloadSelected () {
      const selected = (this.activityInfo?.materials || []).filter((m) =>
        this.selectedMaterials.includes(m.id),
      );
      this.openFiles(selected);
    },

    openFiles (list) {
      if (list.length === 1) {
        window.open(getActivityDownloadUrl(this.visitorId, list[0].id), '_blank');
      } else {
        this.downloadZipClientSide(list);
      }
    },

    async downloadZipClientSide (list) {
      this.downloading = true;
      this.downloadProgress = 0;

      // 每个文件的已接收字节数
      const received = new Array(list.length).fill(0);
      // 每个文件的总字节数（未知则为 0）
      const totals = new Array(list.length).fill(0);

      const updateProgress = () => {
        const totalBytes = totals.reduce((a, b) => a + b, 0);
        const recvBytes = received.reduce((a, b) => a + b, 0);
        if (totalBytes > 0) {
          // 下载阶段占 0-90%
          this.downloadProgress = Math.min(90, Math.round((recvBytes / totalBytes) * 90));
        }
      };

      try {
        // 并行下载所有文件
        const blobs = await Promise.all(
          list.map((item, idx) => {
            const url = getActivityDownloadUrl(this.visitorId, item.id);
            return this.fetchWithProgress(url, (recv, total) => {
              received[idx] = recv;
              totals[idx] = total;
              updateProgress();
            });
          }),
        );

        // 打包阶段 90-99%
        this.downloadProgress = 90;
        const zip = new JSZip();
        blobs.forEach((blob, idx) => {
          zip.file(list[idx].fileName || list[idx].name, blob);
        });
        const zipBlob = await zip.generateAsync({ type: 'blob' }, (meta) => {
          this.downloadProgress = 90 + Math.round(meta.percent * 0.09);
        });

        this.downloadProgress = 100;
        const zipName = (this.activityInfo?.name || '资料包') + '.zip';
        this.triggerBlobDownload(zipBlob, zipName);
      } catch (e) {
        alert('下载失败，请重试');
      } finally {
        this.downloading = false;
        this.downloadProgress = 0;
      }
    },

    async fetchWithProgress (url, onProgress) {
      const response = await fetch(url);
      if (!response.ok) throw new Error('下载失败');

      const contentLength = Number(response.headers.get('Content-Length')) || 0;

      if (!response.body || !window.ReadableStream) {
        const blob = await response.blob();
        onProgress(blob.size, blob.size);
        return blob;
      }

      const reader = response.body.getReader();
      const chunks = [];
      let recv = 0;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        chunks.push(value);
        recv += value.length;
        onProgress(recv, contentLength);
      }

      return new Blob(chunks);
    },

    triggerBlobDownload (blob, filename) {
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
    },

    thumbBgClass (fileName) {
      if (this.isPdf(fileName)) return 'thumb-bg-pdf';
      if (this.isWord(fileName)) return 'thumb-bg-word';
      if (this.isExcel(fileName)) return 'thumb-bg-excel';
      return 'thumb-bg-default';
    },

    isPdf (n) { return n && n.toLowerCase().endsWith('.pdf'); },
    isWord (n) { return n && /\.(doc|docx)$/i.test(n); },
    isExcel (n) { return n && /\.(xls|xlsx)$/i.test(n); },
  },
};
</script>

<style scoped>
/* ===== 统一盒模型，防止任何元素撑出屏幕 ===== */
* {
  box-sizing: border-box;
}

/* ===== 根容器：铺满手机，严格禁止横向溢出 ===== */
.scan-page {
  width: 100%;
  min-height: 100vh;
  overflow-x: hidden;
  background: #f5f7fa;
}

/* ===== 居中占位（loading / error） ===== */
.full-center {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
}
.error-wrap {
  text-align: center;
  padding: 0 24px;
  width: 100%;
}
.error-icon { font-size: 56px; margin-bottom: 16px; }
.error-title { font-size: 18px; font-weight: 600; color: #333; margin-bottom: 8px; }
.error-sub { font-size: 14px; color: #999; }

/* ===== 填写信息页 ===== */
.view-form {
  width: 100%;
  padding: 40px 20px calc(32px + env(safe-area-inset-bottom, 0px));
}

.form-title {
  font-size: 22px;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 4px;
}
.form-sub {
  font-size: 14px;
  color: #999;
  margin-bottom: 32px;
}

.field-group {
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 32px;
  width: 100%;
}

.field {
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 0;
}

.field-label {
  font-size: 14px;
  color: #444;
  font-weight: 500;
  margin-bottom: 8px;
}
.req { color: #ff4d4f; margin-left: 2px; }

.field-input {
  display: block;
  width: 100%;
  height: 50px;
  border: 1px solid #dde1e7;
  border-radius: 10px;
  padding: 0 16px;
  font-size: 16px; /* 防iOS自动缩放 */
  color: #1a1a1a;
  background: #fff;
  outline: none;
  -webkit-appearance: none;
  transition: border-color 0.2s, box-shadow 0.2s;
}
.field-input:focus {
  border-color: #1890ff;
  box-shadow: 0 0 0 3px rgba(24, 144, 255, 0.12);
}

.field-error {
  font-size: 12px;
  color: #ff4d4f;
  margin-top: 5px;
  padding-left: 4px;
}

/* 验证码行 */
.code-row {
  display: flex;
  gap: 10px;
  width: 100%;
  min-width: 0;
}
.code-input {
  flex: 1;
  min-width: 0; /* 防止flex子项撑出容器 */
}
.btn-code {
  flex-shrink: 0;
  width: 110px;
  height: 50px;
  padding: 0;
  border-radius: 10px;
  border: 1px solid #1890ff;
  background: #fff;
  color: #1890ff;
  font-size: 13px;
  white-space: nowrap;
  cursor: pointer;
  transition: all 0.2s;
}
.btn-code:disabled {
  border-color: #d9d9d9;
  color: #bbb;
  cursor: not-allowed;
}

/* 提交前的接口错误提示 */
.submit-error {
  margin-bottom: 12px;
  padding: 10px 14px;
  border-radius: 8px;
  background: #fff2f0;
  border: 1px solid #ffccc7;
  font-size: 14px;
  color: #ff4d4f;
  line-height: 1.5;
  word-break: break-all;
}

/* 提交按钮 */
.btn-submit {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  height: 52px;
  border: none;
  border-radius: 12px;
  background: #1890ff;
  color: #fff;
  font-size: 17px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
}
.btn-submit:active { background: #096dd9; }
.btn-submit:disabled { background: #b0d4f5; cursor: not-allowed; }

/* loading 旋转 */
.loading-spin {
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255, 255, 255, 0.4);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

/* ===== 下载资料页 ===== */
.view-download {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  padding: 20px 16px calc(80px + env(safe-area-inset-bottom, 0px));
  background: #f0f4ff;
}

/* 顶部成功横幅 */
.dl-banner {
  display: flex;
  align-items: center;
  gap: 12px;
  background: #fff;
  border-radius: 14px;
  padding: 14px 16px;
  margin-bottom: 12px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.06);
}
.dl-banner-icon {
  flex-shrink: 0;
  width: 42px;
  height: 42px;
  border-radius: 50%;
  background: linear-gradient(135deg, #52c41a, #73d13d);
  color: #fff;
  font-size: 20px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 3px 10px rgba(82, 196, 26, 0.3);
}
.dl-banner-info { flex: 1; min-width: 0; }
.dl-banner-title { font-size: 15px; font-weight: 700; color: #1a1a1a; margin-bottom: 2px; }
.dl-banner-sub   { font-size: 12px; color: #999; }

/* 全部下载按钮 */
.btn-dl-all {
  flex-shrink: 0;
  height: 34px;
  padding: 0 14px;
  border: 1.5px solid #1890ff;
  border-radius: 17px;
  background: transparent;
  color: #1890ff;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s;
}
.btn-dl-all:active { background: #e6f4ff; }
.btn-dl-all:disabled {
  border-color: #d9d9d9;
  color: #bbb;
  cursor: not-allowed;
}

/* 无资料 */
.dl-empty {
  text-align: center;
  color: #bbb;
  font-size: 14px;
  padding: 48px 0;
  background: #fff;
  border-radius: 14px;
}

/* 文件列表容器 */
.dl-list-wrap {
  background: #fff;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
}

/* 通用行 */
.dl-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-bottom: 1px solid #f5f5f5;
  cursor: pointer;
  transition: background 0.15s;
  min-width: 0;
}
.dl-row:last-child { border-bottom: none; }
.dl-row.is-selected { background: #f0f7ff; }
.dl-row:active { opacity: 0.8; }

/* 全选行 */
.dl-row-header {
  background: #fafafa;
  border-bottom: 1px solid #efefef !important;
  padding: 12px 16px;
}
.dl-row-all-label {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 500;
  color: #333;
}
.dl-count-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 10px;
  background: #1890ff;
  color: #fff;
  font-size: 11px;
  font-weight: 600;
}

/* 文件类型图标块 */
.dl-row-thumb {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
}
.thumb-bg-pdf     { background: #fff1f0; }
.thumb-bg-word    { background: #e6f4ff; }
.thumb-bg-excel   { background: #f6ffed; }
.thumb-bg-default { background: #f9f0ff; }
.thumb-pdf        { color: #ff4d4f; }
.thumb-word       { color: #1890ff; }
.thumb-excel      { color: #52c41a; }
.thumb-default    { color: #722ed1; }

/* 文件名 */
.dl-row-name {
  flex: 1;
  min-width: 0;
  font-size: 14px;
  font-weight: 500;
  color: #1a1a1a;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* 预览按钮 */
.dl-row-preview {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 8px;
  background: #f0f7ff;
  color: #1890ff;
  font-size: 17px;
  text-decoration: none;
  transition: background 0.15s;
}
.dl-row-preview:active { background: #cce4ff; }

/* 自定义 checkbox */
.xz-checkbox {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border: 1.5px solid #d9d9d9;
  border-radius: 4px;
  background: #fff;
  transition: border-color 0.2s, background 0.2s;
}
.xz-checkbox.is-checked,
.xz-checkbox.is-indeterminate {
  border-color: #1890ff;
  background: #1890ff;
}
.xz-checkbox.is-checked .xz-checkbox-inner::after {
  content: '';
  display: block;
  width: 5px;
  height: 9px;
  border: 2px solid #fff;
  border-top: none;
  border-left: none;
  transform: rotate(45deg) translateY(-1px);
}
.xz-checkbox.is-indeterminate .xz-checkbox-inner {
  width: 10px;
  height: 2px;
  background: #fff;
  border-radius: 1px;
}

/* 固定底部操作栏 */
.dl-action-bar {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px calc(12px + env(safe-area-inset-bottom, 0px));
  background: #fff;
  box-shadow: 0 -2px 12px rgba(0, 0, 0, 0.08);
}
.dl-action-hint {
  font-size: 14px;
  color: #666;
}
.btn-dl-selected {
  height: 42px;
  padding: 0 28px;
  border: none;
  border-radius: 21px;
  background: linear-gradient(135deg, #1890ff, #096dd9);
  color: #fff;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  box-shadow: 0 4px 12px rgba(24, 144, 255, 0.35);
  transition: opacity 0.2s, transform 0.15s;
}
.btn-dl-selected:active { opacity: 0.85; transform: scale(0.97); }
.btn-dl-selected:disabled {
  background: #d9d9d9;
  box-shadow: none;
  cursor: not-allowed;
}

/* ===== 安卓微信引导页 ===== */
.view-wechat-android {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 60px 28px 40px;
  min-height: 100vh;
  background: #fff;
  text-align: center;
}
.wxa-icon {
  font-size: 60px;
  margin-bottom: 20px;
}
.wxa-title {
  font-size: 22px;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 10px;
}
.wxa-desc {
  font-size: 14px;
  color: #666;
  margin-bottom: 28px;
  line-height: 1.6;
}
.wxa-steps {
  width: 100%;
  background: #f5f7fa;
  border-radius: 14px;
  padding: 20px 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 32px;
  text-align: left;
}
.wxa-step {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  font-size: 15px;
  color: #333;
  line-height: 1.5;
}
.wxa-step-num {
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #1890ff;
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
}
.wxa-arrow {
  font-size: 13px;
  color: #1890ff;
  font-weight: 500;
  background: #e6f4ff;
  border-radius: 8px;
  padding: 8px 16px;
}

/* ===== 下载进度遮罩 ===== */
.dl-progress-mask {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}
.dl-progress-box {
  width: 260px;
  background: #fff;
  border-radius: 16px;
  padding: 28px 24px 24px;
  text-align: center;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
}
.dl-progress-title {
  font-size: 15px;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 18px;
}
.dl-progress-bar-wrap {
  height: 8px;
  background: #e8edf3;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 10px;
}
.dl-progress-bar {
  height: 100%;
  background: linear-gradient(90deg, #1890ff, #40a9ff);
  border-radius: 4px;
  transition: width 0.3s ease;
  min-width: 4px;
}
.dl-progress-text {
  font-size: 13px;
  color: #888;
}

/* ===== iOS 微信下载提示 ===== */
.dl-ios-tip {
  background: #fffbe6;
  border: 1px solid #ffe58f;
  border-radius: 12px;
  padding: 14px 16px;
  margin-bottom: 12px;
}
.dl-ios-tip-title {
  font-size: 14px;
  font-weight: 600;
  color: #874d00;
  margin-bottom: 10px;
}
.dl-ios-tip-body {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.dl-ios-step {
  font-size: 13px;
  color: #614700;
  line-height: 1.6;
}
</style>
